import './AddRecruiterModal.scss';

import FilterListIcon from '@mui/icons-material/FilterList';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  FormControlLabel,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DismissIcon from 'assets/images/iPrise/Dismiss.svg';
import UpAndDownFilterIcon from 'assets/images/iPrise/UpAndDownFilter.svg';
import { TeamUserRoles } from 'pages/iPriseV2/common/enum/TeamUserRoles';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFetchList } from 'services/common/react-query-common-service';

import StylishLoader from '../../../common/components/StylishLoader';

interface Recruiter {
  id: string;
  firstName: string;
  lastName: string;
  empCode: string;
  teamUserRole: string;
  status: boolean;
  login: {
    id: string;
    email: string;
  };
  isTeamLeader: boolean;
  userAlreadyExists: boolean;
}

interface AddRecruiterModalProps {
  open: boolean;
  onClose: () => void;
  onAddRecruiters: (recruiterIds: string[]) => void;
  teamId?: string;
}

const AddRecruiterModal: React.FC<AddRecruiterModalProps> = ({
  onAddRecruiters,
  onClose,
  open,
  teamId,
}) => {
  const [selectedRecruiters, setSelectedRecruiters] = useState<string[]>([]);
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openTimestamp, setOpenTimestamp] = useState<number>(0);

  // Update timestamp when modal opens to force refetch
  useEffect(() => {
    if (open) {
      setOpenTimestamp(Date.now());
    }
  }, [open]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Build query params
  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.append('skip', String(page * rowsPerPage));
    params.append('take', String(rowsPerPage));
    params.append('search', debouncedSearch);
    params.append('roleType', TeamUserRoles.RECRUITER);
    if (showActiveOnly) {
      params.append('status', 'true');
    }
    return params.toString();
  }, [page, rowsPerPage, debouncedSearch, showActiveOnly]);

  // Fetch recruiters from API
  const { data: recruitersResp, isLoading: isLoadingRecruiters } =
    useFetchList<any>(
      [
        'team-recruiters-modal',
        teamId,
        page,
        rowsPerPage,
        debouncedSearch,
        showActiveOnly,
        openTimestamp,
      ],
      `users/teams/${teamId || ''}?${queryParams}`,
      {
        enabled: !!teamId && open,
      },
    );

  // Extract recruiters from API response
  const recruiters: Recruiter[] = useMemo(
    () => recruitersResp?.data || [],
    [recruitersResp],
  );

  // API handles filtering and pagination, so we use recruiters directly
  const totalCount = recruitersResp?.total || 0;

  const handleClose = useCallback(() => {
    setSelectedRecruiters([]);
    setSearchTerm('');
    setPage(0);
    onClose();
  }, [onClose]);

  const handleToggleRecruiter = useCallback((recruiterId: string) => {
    setSelectedRecruiters((prev) =>
      prev.includes(recruiterId)
        ? prev.filter((id) => id !== recruiterId)
        : [...prev, recruiterId],
    );
  }, []);

  const handleToggleAll = useCallback(
    (recruiters: Recruiter[]) => {
      const allRecruiterIds = recruiters.map((m) => m.id);
      if (selectedRecruiters.length === recruiters.length) {
        setSelectedRecruiters([]);
      } else {
        setSelectedRecruiters(allRecruiterIds);
      }
    },
    [selectedRecruiters.length],
  );

  const handleAddRecruiters = useCallback(async () => {
    if (selectedRecruiters.length > 0) {
      setIsSubmitting(true);

      try {
        await onAddRecruiters(selectedRecruiters);
        handleClose();
      } catch (error) {
        console.error('Failed to add recruiters:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  }, [selectedRecruiters, onAddRecruiters, handleClose]);

  const handleChangePage = useCallback((_event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    },
    [],
  );

  const isAllSelected = useMemo(
    () =>
      recruiters.length > 0 &&
      recruiters.every((recruiter) =>
        selectedRecruiters.includes(recruiter.id),
      ),
    [recruiters, selectedRecruiters],
  );

  return (
    <Dialog
      className="add-recruiter-modal"
      maxWidth="md"
      open={open}
      onClose={handleClose}
      fullWidth>
      <Box className="modal-header">
        <Typography className="modal-title">Add Recruiter</Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={showActiveOnly}
              onChange={(e) => setShowActiveOnly(e.target.checked)}
              className="active-checkbox"
            />
          }
          label="Show Active User Only"
          className="active-control"
        />
        <TextField
          placeholder="Search"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-field"
        />
        <IconButton className="sort-button">
          <img src={UpAndDownFilterIcon} alt="Sort" />
        </IconButton>
        <Button
          variant="contained"
          className="add-button"
          onClick={handleAddRecruiters}
          disabled={selectedRecruiters.length === 0 || isSubmitting}>
          {isSubmitting ? (
            <StylishLoader fullPage={false} className="button" />
          ) : selectedRecruiters.length > 0 ? (
            `Add Recruiter (${selectedRecruiters.length})`
          ) : (
            'Add Recruiter'
          )}
        </Button>
        <IconButton className="close-button" onClick={handleClose}>
          <img src={DismissIcon} alt="Close" />
        </IconButton>
      </Box>

      <Box className="modal-content">
        <Box className="table-wrapper">
          <Box className="table-main">
            <TableContainer className="table-container" sx={{ minHeight: 400 }}>
              <Table stickyHeader className="members-table">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" className="header-cell">
                      <Checkbox
                        checked={isAllSelected}
                        indeterminate={
                          selectedRecruiters.length > 0 && !isAllSelected
                        }
                        onChange={() => handleToggleAll(recruiters)}
                        className="header-checkbox"
                      />
                    </TableCell>
                    <TableCell className="header-cell">Name</TableCell>
                    <TableCell className="header-cell">Email</TableCell>
                    <TableCell className="header-cell">Role</TableCell>
                    <TableCell className="header-cell">Team</TableCell>
                    <TableCell className="header-cell">Status</TableCell>
                    <TableCell align="right" className="header-cell" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoadingRecruiters ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        align="center"
                        className="body-cell"
                        sx={{ padding: '40px 0' }}>
                        <StylishLoader
                          text="Loading recruiters..."
                          className="table"
                        />
                      </TableCell>
                    </TableRow>
                  ) : recruiters.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        align="center"
                        className="body-cell">
                        <Typography
                          sx={{
                            color: '#707070',
                            fontFamily: 'Inter',
                            fontSize: '14px',
                          }}>
                          No recruiters found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    recruiters.map((recruiter) => (
                      <TableRow
                        key={recruiter.id}
                        className="table-row"
                        hover
                        onClick={() => handleToggleRecruiter(recruiter.id)}>
                        <TableCell padding="checkbox" className="body-cell">
                          <Checkbox
                            checked={selectedRecruiters.includes(recruiter.id)}
                            className="row-checkbox"
                          />
                        </TableCell>
                        <TableCell className="body-cell">
                          {recruiter.firstName} {recruiter.lastName}
                        </TableCell>
                        <TableCell className="body-cell">
                          {recruiter.login?.email}
                        </TableCell>
                        <TableCell className="body-cell">
                          {recruiter.teamUserRole}
                        </TableCell>
                        <TableCell className="body-cell">
                          {recruiter.empCode}
                        </TableCell>
                        <TableCell className="body-cell">
                          <Chip
                            label={recruiter.status ? 'Active' : 'Inactive'}
                            className={`status-chip ${
                              !recruiter.status ? 'inactive' : ''
                            }`}
                          />
                        </TableCell>
                        <TableCell align="right" className="body-cell">
                          <IconButton size="small" className="more-btn">
                            <MoreHorizIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box className="filters-sidebar">
              <FilterListIcon className="filters-icon" />
              <Typography className="filters-text">Filters</Typography>
            </Box>
          </Box>
          <TablePagination
            className="table-pagination"
            component="div"
            count={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[50, 100, 150, 200, 250]}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      </Box>
    </Dialog>
  );
};

export default AddRecruiterModal;
